import React, { useState, useEffect, useCallback } from "react";
import {
  listLateFeeRules,
  listLateFeeRulesPortal,
  deleteLateFeeRule,
  deleteLateFeeRulePortal,
} from "../../api/fees/feeApi";
import LateFeeRuleModal from "../../components/fees/LateFeeRuleModal";
import {
  Button,
  Badge,
  Table,
  EmptyState,
  Skeleton,
} from "../../components/ui";
import { Plus, Clock } from "lucide-react";
import { useToast } from "../../context/ToastContext";

const LateFeeRulesPage = ({ isPortal = false }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = isPortal ? await listLateFeeRulesPortal() : await listLateFeeRules();
      setRules(res.data || res || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load late fee rules.");
    } finally {
      setLoading(false);
    }
  }, [isPortal, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this late fee rule?")) return;
    try {
      if (isPortal) await deleteLateFeeRulePortal(id);
      else await deleteLateFeeRule(id);
      toast.success("Late fee rule removed.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to remove late fee rule.");
    }
  };

  const columns = [
    { key: "name", header: "Name" },
    {
      key: "penaltyType",
      header: "Type",
      render: (row) => (
        <span className="capitalize">
          {(row.penaltyType || "").replace(/_/g, " ").toLowerCase()}
        </span>
      ),
    },
    {
      key: "value",
      header: "Value",
      render: (row) =>
        row.penaltyType === "PERCENTAGE"
          ? `${row.penaltyValue}%`
          : `₹${Number(row.penaltyValue || 0).toFixed(2)}`,
    },
    {
      key: "gracePeriodDays",
      header: "Grace period",
      render: (row) => `${row.gracePeriodDays ?? 0} day(s)`,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => (
        <Badge variant={row.isActive ? "success" : "default"}>
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setEditData(row);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-danger-500 hover:text-danger-700"
            onClick={() => handleDelete(row._id)}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
            Late fee rules
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Define penalties and grace periods for overdue invoices. Each fine is
            applied once per fee bill (ledger), not once per fee head.
          </p>
        </div>
        <Button
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => {
            setEditData(null);
            setModalOpen(true);
          }}
        >
          Add rule
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
          <div className="flex gap-4">
            <Skeleton height={12} width="20%" />
            <Skeleton height={12} width="25%" />
            <Skeleton height={12} width="15%" />
            <Skeleton height={12} width="20%" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={16} width="20%" />
              <Skeleton height={16} width="25%" />
              <Skeleton height={16} width="15%" />
              <Skeleton height={16} width="20%" />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          data={rules}
          emptyState={
            <EmptyState
              icon={<Clock />}
              title="No late fee rules"
              description="Create a rule to automatically apply a single penalty per overdue bill after the grace period."
              action={
                <Button
                  leftIcon={<Plus className="h-4 w-4" />}
                  onClick={() => {
                    setEditData(null);
                    setModalOpen(true);
                  }}
                >
                  Add rule
                </Button>
              }
            />
          }
        />
      )}

      <LateFeeRuleModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchData}
        editData={editData}
        isPortal={isPortal}
      />
    </div>
  );
};

export default LateFeeRulesPage;